import CheckboxCard from "@/components/Checkbox/CheckboxCard";
import Button from "@/components/Button";
import type { TagType } from "@/components/Tag/Tag";
import { formatEnumLabel } from "@/pages/billing/finance/commission/_utils";
import type { CommissionCampaignNode } from "@/pages/billing/finance/commission/campaigns/types";

export function sponsorHeadline(campaign: CommissionCampaignNode) {
  return `${campaign.editionYear} — ${campaign.partner.name}`;
}

function campaignCardSupportingText(campaign: CommissionCampaignNode) {
  return `${formatEnumLabel(campaign.payoutCadence)} payout`;
}

function campaignStatusTagType(status: CommissionCampaignNode["status"]): TagType {
  if (status === "ACTIVE") return "success";
  if (status === "DRAFT") return "information";
  return "surface";
}

interface GrpCampaignMultiSelectFieldsProps {
  campaigns: CommissionCampaignNode[];
  selectedIds: string[];
  onChange: (nextIds: string[]) => void;
  disabled?: boolean;
  lockedIds?: string[];
  errorMessage?: string;
}

export function GrpCampaignMultiSelectFields({
  campaigns,
  selectedIds,
  onChange,
  disabled = false,
  lockedIds = [],
  errorMessage
}: GrpCampaignMultiSelectFieldsProps) {
  const bulkDisabled = disabled || lockedIds.length > 0;
  const activeCampaignIds = campaigns.filter((campaign) => campaign.status === "ACTIVE").map((campaign) => campaign.id);
  const allSelected = campaigns.length > 0 && campaigns.every((campaign) => selectedIds.includes(campaign.id));
  const activeSelected =
    activeCampaignIds.length > 0 && activeCampaignIds.every((campaignId) => selectedIds.includes(campaignId));

  const toggleCampaign = (campaignId: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedIds, campaignId]);
      return;
    }
    onChange(selectedIds.filter((id) => id !== campaignId));
  };

  const toggleSelectAll = () => onChange(allSelected ? [] : campaigns.map((campaign) => campaign.id));
  const selectActiveOnly = () => onChange(activeCampaignIds);

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-headline-small">Select the sponsors this FAA authorizes.</h2>
      <p className="text-body-large text-sys-brand-on-surface-variant">
        FAA and IAA changes are saved separately for each sponsor.
      </p>

      {errorMessage ? (
        // MultiStepForm scrolls to `[data-testid="<field name>"]` when a step fails validation.
        <p
          data-testid="selectedCampaignIds"
          tabIndex={-1}
          aria-live="polite"
          className="text-body-medium text-sys-brand-error"
        >
          {errorMessage}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <Button variant="outlined" onClick={toggleSelectAll} disabled={bulkDisabled || campaigns.length === 0}>
          {allSelected ? "Unselect All" : "Select All"}
        </Button>
        <Button
          variant="outlined"
          onClick={selectActiveOnly}
          disabled={bulkDisabled || activeCampaignIds.length === 0 || activeSelected}
        >
          Active Only
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        {campaigns.map((campaign) => (
          <CheckboxCard
            key={campaign.id}
            name={`grp-campaign-${campaign.id}`}
            testId={`grp-campaign-${campaign.id}`}
            headline={sponsorHeadline(campaign)}
            supportingText={campaignCardSupportingText(campaign)}
            tagText={formatEnumLabel(campaign.status)}
            tagType={campaignStatusTagType(campaign.status)}
            checked={selectedIds.includes(campaign.id)}
            disabled={disabled || lockedIds.includes(campaign.id)}
            onChange={(checked) => toggleCampaign(campaign.id, checked)}
          />
        ))}
      </div>
    </div>
  );
}
